import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFilteredCipRegisters,
  selectCipRegisterFilterVisible,
  selectCipRegisterLoading,
  selectFetchCipRegisterLoading,
} from './store/selectors';
import LoadingSpinner from '../../InitialPage/Sidebar/LoadingSpinner';
import { isHeaderVisible } from 'Core/redux/main/selectors';
import { Edit, Trash2 } from 'react-feather';
import { Button, ButtonGroup } from 'react-bootstrap';
import { deleteCipRegister, fetchCipRegisterById } from './store/slice';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import VirtualList from '../components/VirtualList';

const CipRegisterTile = () => {
  const [columnCount, setColumnCount] = useState(3);
  const [columnWidth, setColumnWidth] = useState(300);
  const tileRef = useRef(null);

  const filteredData = useSelector(selectFilteredCipRegisters);
  const isLoading = useSelector(selectCipRegisterLoading);
  const filterVisible = useSelector(selectCipRegisterFilterVisible);
  const isFetchDataLoading = useSelector(selectFetchCipRegisterLoading);
  const headerVisible = useSelector(isHeaderVisible);

  const updateColumnSettings = useCallback(() => {
    if (tileRef.current) {
      const width = tileRef.current.offsetWidth;
      let columns = 3;
      if (width < 576) columns = 1;
      else if (width < 768) columns = 2;
      else if (width < 1200) columns = 3;
      else columns = 4;
      setColumnCount(columns);
      setColumnWidth(Math.max(width / columns, 250));
    }
  }, []);

  useEffect(() => {
    updateColumnSettings();
    window.addEventListener('resize', updateColumnSettings);
    return () => window.removeEventListener('resize', updateColumnSettings);
  }, [updateColumnSettings]);

  const itemData = (filteredData || []).map(cr => <CRTile cr={cr} />);

  return (
    <div
      className={`tile-body ${filterVisible ? 'filter-visible' : ''} ${
        !headerVisible ? 'header-collapsed' : ''
      }`}
      ref={tileRef}
    >
      <div className="d-flex flex-wrap gap-2 mb-3 ms-2 mt-1">
        <button className="btn btn-primary" style={{ backgroundColor: '#2b6cb0', border: 'none', borderRadius: '8px', padding: '6px 20px', fontWeight: 'bold' }}>All</button>
        {['Internal', 'Audit', 'Supplier', 'Complaint'].map(chip => (
          <button key={chip} className="btn btn-light" style={{ backgroundColor: '#e2e8f0', color: '#1a202c', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '6px 20px', fontWeight: 'bold' }}>
            {chip}
          </button>
        ))}
      </div>
      
      {(isLoading || isFetchDataLoading) && <LoadingSpinner />}
      <VirtualList
        columnCount={columnCount}
        rowCount={Math.ceil((filteredData || []).length / columnCount)}
        itemData={itemData}
        columnWidth={columnWidth}
        rowHeight={160}
      />
    </div>
  );
};

const CRTile = ({ cr }) => {
  const dispatch = useDispatch();

  const handleEdit = e => {
    e.stopPropagation();
    dispatch(fetchCipRegisterById(cr.id));
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
    if (result.isConfirmed) {
      dispatch(deleteCipRegister(cr.id));
    }
  };

  const approvalsColor =
    cr.approvalsCount === cr.approvalsTotal
      ? '#28a745'
      : cr.approvalsCount >= cr.approvalsTotal / 2
      ? '#ffc107'
      : '#dc3545';

  return (
    <div key={cr.id} className="tile" style={{ padding: '4px' }}>
      <div
        className={`card p-3 m-1 h-100 ${cr.isNewlyCreated ? 'border-success' : ''}`}
        style={{
          border: '1px solid #71a9e8', // matching blue/grey border
          borderRadius: '14px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          position: 'relative',
        }}
      >
        {/* Actions */}
        <div className="tile-actions">
          <ButtonGroup size="sm">
            <Button variant="light" onClick={handleEdit} title="Edit">
              <Edit size={15} />
            </Button>
            <Button variant="light" onClick={handleDelete} title="Delete">
              <Trash2 size={15} />
            </Button>
          </ButtonGroup>
        </div>

        <div className="d-flex justify-content-between align-items-start mb-2 pe-4">
          <span style={{ fontWeight: '700', color: '#2d3748', fontSize: '0.85rem' }}>
            {cr.crNumber}
          </span>
          <span
            className="badge"
            style={{
              backgroundColor: cr.status === 'Verification' ? '#2b6cb0' : 
                               cr.status === 'Investigation' ? '#dd6b20' : 
                               cr.status === 'Validation' ? '#38a169' : 
                               cr.status === 'Closed' ? '#2f855a' : '#718096',
              color: '#fff',
              padding: '6px 12px',
              borderRadius: '8px',
              fontSize: '0.75rem',
              fontWeight: '600',
            }}
          >
            {cr.status || 'Draft'}
          </span>
        </div>

        <h5 className="mb-2 fw-bold" style={{ fontSize: '1.05rem', color: '#1a202c', lineHeight: '1.3' }}>
          {cr.title}
        </h5>

        <div className="mb-0 mt-auto" style={{ fontSize: '0.82rem', color: '#718096' }}>
          Owner: {cr.initiator}
        </div>
      </div>
    </div>
  );
};

export default CipRegisterTile;
